import React, { useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form, Alert, Row } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import * as Types from 'common/utils/Types'
import { Navigate } from 'react-router-dom'

interface PossibleValue {
    label: string
    value: string | number
}

interface Props {
    onSuccess: string | ((result: unknown) => void)
    static?: boolean
    variableName?: string
    variableType: "string" | "int"
    possibleValues?: PossibleValue[]
    possibleValuesPromise?: Promise<PossibleValue[]>
}

function EditVariableWorkflow(props: Props) {

    const [redirect, setRedirect] = useState<string | null>(null)
    const isAdding = props.variableName === undefined

    if (props.variableType !== "int" && props.variableType !== "string") {
        return <Alert variant="danger">Programmer's error: unrecognized variable type '{props.variableType}'</Alert>
    }

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        const valuePromise = props.variableName
            ? API.getVariable(props.variableName, props.variableType)
                .then(response => ({ previousValue: (response.data as any)["value"] }))
            : Promise.resolve({} as { previousValue?: unknown; fetchedPossibleValues?: PossibleValue[] })

        if (props.possibleValuesPromise) {
            return valuePromise.then(
                (valueResult: any) => props.possibleValuesPromise!.then(
                    (possibleResult: PossibleValue[]) => {
                        valueResult.fetchedPossibleValues = possibleResult
                        return valueResult
                    })
            )
        } else {
            return valuePromise
        }
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const lr = loading_result as any
        const formEl = form(_parameters, lr.previousValue, lr.fetchedPossibleValues || props.possibleValues, completion)
        return { component: formEl, title: `${isAdding ? "Add" : "Edit"} ${props.variableType} variable` as string }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        const input = user_input as { name: string; value: string | number }
        const value = props.variableType === "int" ? parseInt(String(input.value)) : input.value
        return API.setVariable(input.name, value, props.variableType)
    }

    const completionCallback = (execution_result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(execution_result && (execution_result as any).data)
        }
    }

    function form(_parameters: unknown, previousValue: unknown, possibleValues: PossibleValue[] | undefined, completion: (input: unknown) => void) {

        const schema = yup.object().shape({
            value: props.variableType === "int" ? yup.number().required() : yup.string().required(),
            name: yup.string().required()
        })

        const initialValues = {
            value: previousValue || "",
            name: props.variableName || ""
        }

        return <>
            <Formik
                validationSchema={schema}
                onSubmit={completion as any}
                initialValues={initialValues as any}
            >
                {({
                    handleSubmit,
                    handleChange,
                    values,
                    errors,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>

                        <Form.Group as={Row} className="mb-3" controlId="formName">
                            <Form.Label column sm="2">Name</Form.Label>
                            <Col sm="10">
                                <Form.Control
                                    type='text'
                                    name='name'
                                    placeholder="Name"
                                    value={(values as any).name}
                                    onChange={handleChange}
                                    isInvalid={!!(errors as any).name}
                                    readOnly={!isAdding}
                                />
                                <Form.Control.Feedback type="invalid">{(errors as any).name}</Form.Control.Feedback>
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3" controlId="formValue">
                            <Form.Label column sm="2">Value</Form.Label>
                            <Col sm="10">
                                {possibleValues ?
                                    <Form.Control
                                        as='select'
                                        name='value'
                                        value={(values as any).value}
                                        onChange={handleChange}
                                        isInvalid={!!(errors as any).value}
                                    >
                                        {(values as any).value ? " " : <option></option>}
                                        {possibleValues.map(obj => <option value={obj.value} key={`value_${obj.value}`}>{obj.label}</option>)}
                                    </Form.Control>
                                    :
                                    <Form.Control
                                        type='text'
                                        placeholder='Value'
                                        name='value'
                                        value={(values as any).value}
                                        onChange={handleChange}
                                        isInvalid={!!(errors as any).value}
                                    />
                                }

                                <Form.Control.Feedback type="invalid">{(errors as any).value}</Form.Control.Feedback>
                            </Col>
                        </Form.Group>

                        <Button variant="primary" type="submit">Save</Button>

                    </Form>
                )}
            </Formik>
        </>
    }

    return <OperationWorkflowModal
        static={props.static}
        loadingStep={loadingStep}
        userInputStep={userInputStep}
        executionStep={executionStep}
        completionCallback={completionCallback}
    />
}

export default EditVariableWorkflow
